export type UserLoginProfile = {
  displayName?: string;
  name?: string;
  department?: string;
  departments?: string[];
  registeredAt?: string;
  firstLogin?: string;
  lastLogin?: string;
  level?: "basis" | "standard" | "erweitert" | "compliance";
  managerEmail?: string;
  trainingExempt?: boolean;
  trainingExemptFrom?: string;
  trainingExemptUntil?: string;
  // Sammelaccount: das gemeinsame Konto selbst bzw. eine Person dahinter, die
  // sich nur mit der Mitarbeiternummer anmeldet.
  sharedAccountHost?: boolean;
  sharedMember?: boolean;
  // Vom Admin deaktiviert: kein Zugang, zählt nirgends mit, Daten bleiben erhalten.
  deactivated?: boolean;
  deactivatedAt?: string;
  deactivatedBy?: string;
};

// Deaktivierte Konten verschwinden aus allen Listen, Quoten, Berichten und
// Lizenzzählungen; nur die Mitarbeiterliste (`deactivated=1`) zeigt sie dem
// Admin zum Ansehen und Reaktivieren. Spiegel von isDeactivated() in api/_modulePlan.js.
export function isDeactivated(profile: UserLoginProfile | null | undefined): boolean {
  return profile?.deactivated === true;
}

// Ein Sammelkonto ist ein Zugang, kein Schulungsteilnehmer: es taucht in keiner
// Auswahl, Gruppe, Kampagne oder Quote auf. Verwaltet wird es in der
// Mitarbeiterliste, die es über `includeShared=1` gezielt anfordert.
export function isSharedAccountHost(profile: UserLoginProfile | null | undefined): boolean {
  return profile?.sharedAccountHost === true;
}

// Hinter der Adresse eines Sammelaccount-Mitglieds steckt kein Postfach – Mails
// und Phishing-Simulationen können es nicht erreichen.
export function hasMailbox(profile: UserLoginProfile | null | undefined): boolean {
  return !isSharedAccountHost(profile) && profile?.sharedMember !== true;
}

const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function berlinToday(now: number = Date.now()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(now));
}

type ExemptionFields = Pick<UserLoginProfile, "trainingExempt" | "trainingExemptFrom" | "trainingExemptUntil">;

export type TrainingExemptionState = "none" | "active" | "scheduled" | "ended";

// Freistellung mit optionalem Zeitraum (beide Tage einschließlich, Berliner Zeit).
// Spiegel von isTrainingExempt() in api/_modulePlan.js.
export function trainingExemptionState(profile: ExemptionFields | null | undefined, now: number = Date.now()): TrainingExemptionState {
  if (profile?.trainingExempt !== true) return "none";
  const from = profile.trainingExemptFrom && ISO_DAY_RE.test(profile.trainingExemptFrom) ? profile.trainingExemptFrom : "";
  const until = profile.trainingExemptUntil && ISO_DAY_RE.test(profile.trainingExemptUntil) ? profile.trainingExemptUntil : "";
  const today = berlinToday(now);
  if (from && today < from) return "scheduled";
  if (until && today > until) return "ended";
  return "active";
}

// Von der Schulungspflicht ausgenommen ("muss nicht machen"). Solche User bleiben
// sichtbar, fallen aber aus ALLEN Kennzahlen/Quoten/Risiko-Berechnungen heraus.
export function isTrainingExempt(profile: ExemptionFields | null | undefined): boolean {
  return trainingExemptionState(profile) === "active";
}

export type LoggedInDirectoryUser = {
  id: string;
  email: string;
  display_name: string | null;
  department: string | null;
};

// Deaktivierte Konten gelten als nicht angemeldet – so zählen sie nirgends mit.
export function hasSherloxAwareLogin(profile: UserLoginProfile | null | undefined): boolean {
  if (isDeactivated(profile)) return false;
  return Boolean(profile?.lastLogin || profile?.firstLogin || profile?.registeredAt);
}

export function collectExemptEmails(profiles: Record<string, UserLoginProfile>): Set<string> {
  const emails = new Set<string>();
  Object.entries(profiles).forEach(([email, profile]) => {
    const normalizedEmail = email.toLowerCase().trim();
    if (normalizedEmail && isTrainingExempt(profile)) emails.add(normalizedEmail);
  });
  return emails;
}

export function collectLoggedInEmails(profiles: Record<string, UserLoginProfile>): Set<string> {
  const emails = new Set<string>();
  Object.entries(profiles).forEach(([email, profile]) => {
    const normalizedEmail = email.toLowerCase().trim();
    if (normalizedEmail && hasSherloxAwareLogin(profile) && !isSharedAccountHost(profile)) emails.add(normalizedEmail);
  });
  return emails;
}

function emailToDisplayName(email: string): string {
  const localPart = email.split("@")[0] ?? email;
  return localPart
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, ch => ch.toUpperCase())
    .trim() || email;
}

function getProfileDepartment(profile: UserLoginProfile): string | null {
  const firstDepartment = Array.isArray(profile.departments)
    ? profile.departments.find(value => typeof value === "string" && value.trim())
    : undefined;
  return (firstDepartment ?? profile.department ?? "").trim() || null;
}

export function buildLoggedInDirectoryUsers(
  directoryUsers: LoggedInDirectoryUser[],
  profiles: Record<string, UserLoginProfile>,
  // `mailboxOnly` für alles, was eine Mail verschickt (Phishing): Mitglieder eines
  // Sammelaccounts haben keine. Sammelkonten selbst fallen immer raus.
  { mailboxOnly = false }: { mailboxOnly?: boolean } = {},
): LoggedInDirectoryUser[] {
  const byEmail = new Map<string, LoggedInDirectoryUser>();
  const profilesByEmail = new Map<string, UserLoginProfile>();
  directoryUsers.forEach(user => {
    const normalizedEmail = (user.email ?? "").toLowerCase().trim();
    if (normalizedEmail) byEmail.set(normalizedEmail, user);
  });
  Object.entries(profiles).forEach(([email, profile]) => {
    const normalizedEmail = email.toLowerCase().trim();
    if (normalizedEmail) profilesByEmail.set(normalizedEmail, profile);
  });

  // Nur wer auch im Verzeichnis steht: ein Profil ohne users-Zeile gehört einer
  // gelöschten Person und darf nirgends mitzählen (siehe hooks/useTenantPeople.ts).
  return Array.from(collectLoggedInEmails(profiles))
    .filter(email => byEmail.has(email))
    .filter(email => !mailboxOnly || hasMailbox(profilesByEmail.get(email)))
    .map(email => {
      const directoryUser = byEmail.get(email);
      const profile = profilesByEmail.get(email) ?? {};
      const displayName = (profile.displayName ?? profile.name ?? "").trim();
      return {
        id: directoryUser?.id ?? email,
        email,
        display_name: directoryUser?.display_name ?? (displayName || emailToDisplayName(email)),
        department: directoryUser?.department ?? getProfileDepartment(profile),
      };
    })
    .sort((a, b) => (a.display_name ?? a.email).localeCompare(b.display_name ?? b.email, "de"));
}

// Wie buildLoggedInDirectoryUsers, aber liefert ALLE synchronisierten User
// (auch solche, die sich noch nie in AWARE eingeloggt haben). Wird für die
// Gruppen-/Abteilungs-Ansicht gebraucht: die Microsoft-Entra-Abteilung soll für
// jeden Verzeichnis-User als Gruppenmitgliedschaft erkannt werden, unabhängig vom Login.
// `department` ist hier immer die Entra-Abteilung (aus der users-Tabelle).
export function buildAllDirectoryUsers(
  directoryUsers: LoggedInDirectoryUser[],
  profiles: Record<string, UserLoginProfile>,
): LoggedInDirectoryUser[] {
  const byEmail = new Map<string, LoggedInDirectoryUser>();

  directoryUsers.forEach(user => {
    const normalizedEmail = (user.email ?? "").toLowerCase().trim();
    if (!normalizedEmail) return;
    const profile = profiles[normalizedEmail];
    // Das gemeinsame Konto eines Sammelaccounts gehört in keine Gruppe – geschult
    // wird bei den Personen dahinter, die hier einzeln stehen.
    if (isSharedAccountHost(profile) || isDeactivated(profile)) return;
    const profileName = (profile?.displayName ?? profile?.name ?? "").trim();
    byEmail.set(normalizedEmail, {
      id: user.id ?? normalizedEmail,
      email: normalizedEmail,
      display_name: user.display_name?.trim() || profileName || emailToDisplayName(normalizedEmail),
      department: (user.department ?? "").trim() || null,
    });
  });

  // User, die nur ein Profil haben (z.B. eingeloggt, aber noch nicht im Sync) ergänzen.
  Object.entries(profiles).forEach(([rawEmail, profile]) => {
    const normalizedEmail = rawEmail.toLowerCase().trim();
    if (!normalizedEmail || byEmail.has(normalizedEmail) || isDeactivated(profile)) return;
    const profileName = (profile.displayName ?? profile.name ?? "").trim();
    byEmail.set(normalizedEmail, {
      id: normalizedEmail,
      email: normalizedEmail,
      display_name: profileName || emailToDisplayName(normalizedEmail),
      department: getProfileDepartment(profile),
    });
  });

  return Array.from(byEmail.values())
    .sort((a, b) => (a.display_name ?? a.email).localeCompare(b.display_name ?? b.email, "de"));
}
